import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from './disposerBag'

export interface OrbitCameraOptions {
  name?: string
  alpha: number
  beta: number
  radius: number
  target: BABYLON.Vector3
  /** Room height — keeps the eye below the ceiling at the widest allowed zoom-out/tilt. */
  roomHeight: number
  /** How far the camera is allowed to zoom out before the ceiling clamp kicks in — defaults to 1.8x the starting radius. */
  radiusReach?: number
  eyeMargin?: number
  lowerRadiusLimit?: number
  lowerBetaLimit?: number
  upperBetaLimit?: number
  wheelPrecision?: number
  panningSensibility?: number
}

export interface OrbitCameraResult {
  camera: BABYLON.ArcRotateCamera
}

/**
 * Bounds-safe `ArcRotateCamera` — caps `upperRadiusLimit` so the worst case
 * (max zoom-out at the shallowest allowed tilt) still keeps the eye under
 * the room's ceiling. Generalizes the `(roomHeight - eyeMargin) / cos(beta)`
 * guard SpacesStudio worked out by hand, so it can be reused for any room
 * height instead of re-deriving the math per screen.
 */
export function buildOrbitCamera(scene: BABYLON.Scene, canvas: HTMLCanvasElement, bag: DisposerBag, opts: OrbitCameraOptions): OrbitCameraResult {
  const lowerBeta = opts.lowerBetaLimit ?? 1.15
  const eyeMargin = opts.eyeMargin ?? 1.4
  const radiusReach = opts.radiusReach ?? opts.radius * 1.8

  const cam = bag.track(new BABYLON.ArcRotateCamera(opts.name ?? 'orbitkit-cam', opts.alpha, opts.beta, opts.radius, opts.target.clone(), scene))
  cam.minZ = 0.05
  cam.lowerRadiusLimit = opts.lowerRadiusLimit ?? 2.5
  cam.upperRadiusLimit = Math.min(radiusReach, (opts.roomHeight - eyeMargin) / Math.cos(lowerBeta))
  cam.lowerBetaLimit = lowerBeta
  cam.upperBetaLimit = opts.upperBetaLimit ?? 1.5
  cam.wheelPrecision = opts.wheelPrecision ?? 30
  cam.panningSensibility = opts.panningSensibility ?? 900
  cam.attachControl(canvas, true)
  scene.activeCamera = cam

  return { camera: cam }
}
